import React, { useCallback, useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { PhotoThumb } from "./Photos";

const iso = (d) => d.toLocaleDateString("en-CA");
const ago = (n) => { const x = new Date(); x.setDate(x.getDate() - n); return iso(x); };
const pretty = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const when = (t) => (t ? new Date(t).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const Tick = ({ x }) => <span className={`mr-1 inline-block rounded px-1.5 py-0.5 text-xs font-semibold ${x.done ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>{x.done ? "✓" : "✗"} {x.text}</span>;

const DayCard = ({ d, photoBase }) => (
  <div className="space-y-1.5 rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid={`history-day-${d.date}`}>
    <p className="font-bold text-slate-900">{pretty(d.date)}</p>
    {d.plans.map((p, i) => <p key={i} className="text-slate-700"><b>Plan</b> <span className="text-xs text-slate-500">({p.teacher_name}, saved {when(p.saved_at)})</span>: {[...p.activities, ...p.chips].join(", ")}{p.topic ? ` — ${p.topic}` : ""}{p.text ? ` — ${p.text}` : ""}</p>)}
    {d.marks.map((m, i) => <p key={i} className="text-slate-700" data-testid={`history-mark-${d.date}-${i}`}><b>Teacher</b> <span className="text-xs text-slate-500">({m.teacher_name}, saved {when(m.saved_at)})</span>: {m.label}{m.note ? ` — “${m.note}”` : ""}
      {(m.grade || m.marks != null) && <span className="ml-1 rounded-full bg-violet-100 px-2 text-xs font-bold text-violet-800">Test: {m.grade ? `Grade ${m.grade}` : `${m.marks}${m.out_of ? ` / ${m.out_of}` : ""}`}</span>}</p>)}
    {d.class_items.length > 0 && <div><b>Done in class:</b> {d.class_items.map((x) => <Tick key={x.id} x={x} />)}</div>}
    {d.homework.length > 0 && <div data-testid={`history-hw-${d.date}`}><b>Homework:</b> {d.homework.map((x) => <Tick key={x.id} x={x} />)}</div>}
    <p className="text-slate-700"><b>From home:</b> {d.home.filled ? `filled ${when(d.home.saved_at)}` : "not filled (counts as done)"}{d.home.note ? ` — Extra work: ${d.home.note}` : ""}{d.home.help ? ` — Needs help: ${d.home.help}` : ""}</p>
    {(d.home.like != null || d.home.review) && <p className="text-pink-700">{d.home.like ? "❤️ Liked" : ""}{d.home.review ? ` “${d.home.review}”` : ""}</p>}
    {d.photos.length > 0 && <div className="flex flex-wrap gap-1.5">{d.photos.map((p) => (p.expired
      ? <span key={p.id} className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-500">Photo expired (3 months)</span>
      : <PhotoThumb key={p.id} url={`${photoBase}/${p.id}`} testid={`history-photo-${p.id}`} />))}</div>}
  </div>
);

export const DiaryHistory = ({ url, pdfUrl, photoBase }) => {
  const [frm, setFrm] = useState(ago(6));
  const [to, setTo] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setData((await api.get(url, { params: { frm, to } })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load history"); } finally { setBusy(false); }
  }, [url, frm, to]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const download = async () => {
    try {
      const r = await api.get(pdfUrl, { params: { frm, to }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `PTG-diary-${frm}-to-${to}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Could not download PDF"); }
  };
  return (
    <div className="space-y-3" data-testid="diary-history">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-600">From<input type="date" value={frm} max={to} onChange={(e) => setFrm(e.target.value)} className="ml-1 h-9 rounded border border-slate-300 px-1" data-testid="history-from" /></label>
        <label className="text-xs text-slate-600">To<input type="date" value={to} min={frm} onChange={(e) => setTo(e.target.value)} className="ml-1 h-9 rounded border border-slate-300 px-1" data-testid="history-to" /></label>
        <Button size="sm" onClick={load} disabled={busy} data-testid="history-show">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Show</Button>
        <Button size="sm" variant="outline" onClick={download} data-testid="history-pdf"><Download className="mr-1 h-4 w-4" />Download PDF</Button>
      </div>
      {data && <p className="text-xs text-slate-500" data-testid="history-summary">{data.student.name} · {data.days.length} day(s) with entries</p>}
      {data && data.days.length === 0 && <p className="text-sm text-slate-500" data-testid="history-empty">No diary entries in these dates.</p>}
      {data?.days.map((d) => <DayCard key={d.date} d={d} photoBase={photoBase} />)}
    </div>
  );
};
